import { CustomState } from "@iewc/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,

    /* Verndale Custom Feature - New variables */
    invoiceTypeList: state.customSystemList["InvoiceTypes"] ?? [],
});

type Props = ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailsTotalStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabelText?: TypographyProps;
    subtotalValueText?: TypographyProps;
    discountsLabelText?: TypographyProps;
    discountsValueText?: TypographyProps;
    shippingAndHandlingLabelText?: TypographyProps;
    shippingAndHandlingValueText?: TypographyProps;
    otherChargesLabelText?: TypographyProps;
    otherChargesValueText?: TypographyProps;
    taxLabelText?: TypographyProps;
    taxValueText?: TypographyProps;
    totalLabelText?: TypographyProps;
    totalValueText?: TypographyProps;

    /* Verndale Custom Feature - New styles */
    fullWidthGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    horizontalRuleWrapper?: InjectableCss;
}

export const totalStyles: InvoiceDetailsTotalStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: ${getColor("common.accent")};
            padding: 20px;
        `,
    },
    labelGridItem: { width: 6 },
    valueGridItem: { width: 6 },
    subtotalValueText: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValueText: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingValueText: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValueText: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValueText: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabelText: { weight: "bold" },
    totalValueText: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
};

const styles = totalStyles;

const InvoiceDetailsTotal = ({ enableVat, showPricing, invoiceTypeList }: Props) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    if (!invoice || !showPricing) {
        return null;
    }

    /* Verndale Custom Feature - Get label from invoice type */
    const showInvoiceTypeLabel = () => {
        const typeFromList = invoiceTypeList.find(item => item.name === invoice.invoiceType);
        if (typeFromList) {
            return typeFromList.description ?? "";
        }
        return invoice.invoiceType;
    };

    /* Verndale Custom Feature - Adjust total box presentation */
    return (
        <GridContainer {...styles.container} data-test-selector="invoiceDetails_total">
            {/* Verndale Custom Feature - Add heading section to total box */}
            <GridItem {...styles.fullWidthGridItem}>
                <Typography {...styles.headingText}>{translate("{0} Summary", showInvoiceTypeLabel())}</Typography>
            </GridItem>

            <GridItem {...styles.labelGridItem}>
                {/* Verndale Custom Feature - Change translation */}
                <Typography {...styles.subtotalLabelText}>{translate("Material")}:</Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValueText}>{invoice.productTotalDisplay}</Typography>
            </GridItem>

            {invoice.discountAmount > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabelText}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.discountsValueText}>{invoice.discountAmountDisplay}</Typography>
                    </GridItem>
                </>
            )}

            {/* Verndale Custom Feature - Always displayed */}
            <GridItem {...styles.labelGridItem}>
                {/* Verndale Custom Feature - Change translation */}
                <Typography {...styles.shippingAndHandlingLabelText}>{translate("Shipping")}:</Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.shippingAndHandlingValueText}>
                    {/* Verndale Custom Feature - Adjust logic to show freightTotalAmmount */}
                    {invoice.shippingAndHandling !== 0 && invoice.shippingAndHandlingDisplay}
                    {invoice.shippingAndHandling === 0 &&
                        invoice.properties["freightTotalAmount"] &&
                        invoice.shippingAndHandlingDisplay}
                    {invoice.shippingAndHandling === 0 && !invoice.properties["freightTotalAmount"] && translate("---")}
                </Typography>
            </GridItem>

            {invoice.otherCharges > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.otherChargesLabelText}>{translate("Other Charges")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.otherChargesValueText}>{invoice.otherChargesDisplay}</Typography>
                    </GridItem>
                </>
            )}

            {/* Verndale Custom Feature - Add section for subtotal  */}
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabelText}>{translate("Subtotal")}:</Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValueText}>
                    {invoice.properties["itemsTotal"] && invoice.invoiceTotalDisplay}
                    {!invoice.properties["itemsTotal"] && "$0.00"}
                </Typography>
            </GridItem>

            {(!invoice.invoiceHistoryTaxes || invoice.invoiceHistoryTaxes.length === 0) && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        {/* Verndale Custom Feature - Change translation */}
                        <Typography {...styles.taxLabelText}>{translate("Sales Tax")}:</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxValueText}>{invoice.taxAmountDisplay}</Typography>
                    </GridItem>
                </>
            )}

            {invoice.invoiceHistoryTaxes &&
                invoice
                    .invoiceHistoryTaxes!.sort(tax => tax.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    {/* Verndale Custom Feature - Change translation */}
                                    <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValueText}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}

            {/* Verndale Custom Feature - Add horizonla rule */}
            <GridItem {...styles.fullWidthGridItem}>
                <StyledWrapper {...styles.horizontalRuleWrapper} />
            </GridItem>

            <GridItem {...styles.labelGridItem}>
                {/* Verndale Custom Feature - Change translation */}
                <Typography {...styles.totalLabelText}>{translate("Total")}</Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.totalValueText}>{invoice.invoiceTotalDisplay}</Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailsTotal as any) as any,
    definition: {
        group: "Invoice Details",
        displayName: "Total",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
